import React, {
  useState, useContext, useEffect, memo
} from 'react';
import GlobalContext from '../../contexts/GlobalContext';
import parse from 'html-react-parser';
import { useUpdateAppData } from '../../hooks/services/ups';
import { AppDataList } from '../../types/common';
import {
  SMSPopupInfo, payloadInfo, SMSInputValidationInfo, countryPhoneCode, numbers, validUSPhoneNum
} from '../../types/sms';
import {
  getNumericPhoneNumber, getValidAreaCode, addSpace, formatPhoneNum
} from '../../utils/sms';

type Props = {
  smsPopupInfo: SMSPopupInfo;
  setSmsPopupInfo: React.Dispatch<React.SetStateAction<SMSPopupInfo>>;
};

const SMSPopup = (props: Props): JSX.Element => {
  const { globalContext } = useContext(GlobalContext);
  const [updateAppData] = useUpdateAppData({});
  const {
    smsPopupInfo,
    setSmsPopupInfo
  } = props;
  const [inputInfo, setInputInfo] = useState<SMSInputValidationInfo>({
    validUSAreaCode: [],
    isRedBackground: false,
    isNumberError: false,
    isAreaCodeError: false,
    isPhoneNumError: false,
    isAPIError: false
  });
  const [phoneNum, setPhoneNum] = useState('');
  const i18n = globalContext.i18n;
  const eventCategory = 'SMSOptin';
  const countryCodeNumberClass = smsPopupInfo?.countryCode === 'UK' ? 'message-modal__number--uk' : 'message-modal__number--us';
  const handleAddSpace = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    setPhoneNum(addSpace(e));
  };
  const handleFormatPhoneNum = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPhoneNum(formatPhoneNum(e));
  };
  const handleValidationErrorMessage = (
    errorobj?: SMSInputValidationInfo
  ): void => {
    let currentErrorInfo = {
      isRedBackground: false,
      isNumberError: false,
      isAreaCodeError: false,
      isPhoneNumError: false,
      isAPIError: false
    };

    currentErrorInfo = {
      ...currentErrorInfo,
      ...errorobj
    };

    setInputInfo({
      ...inputInfo,
      ...currentErrorInfo
    });
  };
  const validatePhoneNum = (phoneNumberParam?: string, isConfirmBtnClicked?: boolean): boolean => {
    const formattedPhoneNum = isConfirmBtnClicked ? phoneNum : phoneNumberParam;
    const areaCode = formattedPhoneNum.length === 1 ? formattedPhoneNum : formattedPhoneNum.substring(1, 4);
    const currentPhoneNum = areaCode + formattedPhoneNum.substring(6, 9) + formattedPhoneNum.substring(10);

    if (!isConfirmBtnClicked && (!currentPhoneNum || !formattedPhoneNum)) {
      handleValidationErrorMessage(); // empty phone number

      return false;
    }

    if (isConfirmBtnClicked && !formattedPhoneNum.match(validUSPhoneNum)) {
      handleValidationErrorMessage({ isRedBackground: true, isPhoneNumError: true });

      return false;
    }

    if (!currentPhoneNum.match(numbers)) {
      handleValidationErrorMessage({ isRedBackground: true, isNumberError: true });

      return false;
    }

    if (inputInfo.validUSAreaCode.indexOf(parseInt(areaCode, 10)) === -1) {
      handleValidationErrorMessage({ isRedBackground: true, isAreaCodeError: true });

      return false;
    }

    if (!formattedPhoneNum.match(validUSPhoneNum)) {
      handleValidationErrorMessage({ isRedBackground: true, isPhoneNumError: true });

      return false;
    }

    handleValidationErrorMessage();

    return true;
  };
  const formatAndValidatePhoneNum = (e: React.ChangeEvent<HTMLInputElement>): void => {
    handleFormatPhoneNum(e);

    if (validatePhoneNum(e.currentTarget.value)) {
      globalContext.gtm.pushVersionedEvent('2.x', eventCategory, 'TypedValidMobileNumber');
    }
  };
  const handleConfirmBtn = (): void => {
    const previousPhoneNum = smsPopupInfo && smsPopupInfo?.phoneNumber;

    if (validatePhoneNum(phoneNum, true)) {
      let smsInfoValue: payloadInfo = {
        optInPopupSeen: true,
        optedIn: true,
        phoneNumber: countryPhoneCode[smsPopupInfo?.countryCode || 'US'] + getNumericPhoneNumber(phoneNum),
        namespaceId: globalContext.liquid.namespaceID
      };

      if (previousPhoneNum && previousPhoneNum !== smsInfoValue.phoneNumber) {
        smsInfoValue.optedInSMSSentDateTime = null;
      }

      const payload: AppDataList = [{
        key: 'SMSInfo',
        value: JSON.stringify(smsInfoValue)
      }];

      globalContext.gtm.pushVersionedEvent('2.x', eventCategory, 'TypedValidMobileNumber');

      updateAppData(payload, {
        onError: () => {
          handleValidationErrorMessage({ isRedBackground: true, isAPIError: true });
          globalContext.gtm.pushVersionedEvent('2.x', eventCategory, 'ClickConfirmButton', 'status=failure, failureReason= api error');
        },
        onSuccess: (data) => {
          const newSMSPopupInfo = {
            ...smsPopupInfo,
            showSmsPopup: false,
            showThanksPopup: true,
            phoneNumber: phoneNum,
            optInPopupSeen: true
          };

          setSmsPopupInfo(newSMSPopupInfo);
          sessionStorage.setItem('smsPopupInfo', JSON.stringify(newSMSPopupInfo));
          setTimeout(function () {
            setSmsPopupInfo({ ...newSMSPopupInfo, hideSMS: true });
            sessionStorage.setItem('smsPopupInfo', JSON.stringify({ ...newSMSPopupInfo, hideSMS: true }));
          }, 2000);
          globalContext.gtm.pushVersionedEvent('2.x', eventCategory, 'ClickConfirmButton', 'status= success');
        }
      });
    } else {
      globalContext.gtm.pushVersionedEvent('2.x', eventCategory, 'ClickConfirmButton', 'status=failure, failureReason= inValid phone number');
    }
  };
  const handleNoThanks = (): void => {
    const isFirstTimeVisit = !(smsPopupInfo && smsPopupInfo?.optInPopupSeen);
    const payload: AppDataList = [{
      key: 'SMSInfo',
      value: JSON.stringify({
        optInPopupSeen: true,
        namespaceId: globalContext.liquid.namespaceID
      })
    }];

    if (isFirstTimeVisit) {
      updateAppData(payload, {
        onError: (err) => {
          console.log('error in confirm', err);
        },
        onSuccess: (data) => {
          const newSMSPopupInfo = { ...smsPopupInfo, showSmsPopup: false, optInPopupSeen: true };

          setSmsPopupInfo(newSMSPopupInfo);
          sessionStorage.setItem('smsPopupInfo', JSON.stringify(newSMSPopupInfo));
          setTimeout(function () {
            setSmsPopupInfo({ ...newSMSPopupInfo, hideSMS: true });
            sessionStorage.setItem('smsPopupInfo', JSON.stringify({ ...newSMSPopupInfo, hideSMS: true }));
          }, 150);
        }
      });
    } else {
      const newSMSPopupInfo = { ...smsPopupInfo, showSmsPopup: false, optInPopupSeen: true };

      setSmsPopupInfo(newSMSPopupInfo);
      sessionStorage.setItem('smsPopupInfo', JSON.stringify(newSMSPopupInfo));
      setTimeout(function () {
        setSmsPopupInfo({ ...newSMSPopupInfo, hideSMS: true });
        sessionStorage.setItem('smsPopupInfo', JSON.stringify({ ...newSMSPopupInfo, hideSMS: true }));
      }, 150);
    }

    globalContext.gtm.pushVersionedEvent('2.x', eventCategory, 'ClickNoThanksButton');
  };
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPhoneNum(e.currentTarget.value);
    globalContext.gtm.pushVersionedEvent('2.x', eventCategory, 'TypedMobileNumber');
  };

  useEffect(() => {
    setInputInfo({ ...inputInfo, validUSAreaCode: getValidAreaCode() });

    // TODO: add Virtual Page and change label for Account Page after Account page React Migration
    if (smsPopupInfo && smsPopupInfo.isFirstBoxShipped) {
      globalContext.gtm.setVirtualPage('SMS_Optin_Modal_Login');
      globalContext.gtm.pushVersionedEvent('2.x', eventCategory, 'SysOptinModalShown', 'source= login');
    } else {
      globalContext.gtm.setVirtualPage('SMS_Optin_Modal_Activation');
      globalContext.gtm.pushVersionedEvent('2.x', eventCategory, 'SysOptinModalShown', 'source= subscription');
    }
  }, []);

  return (
    <div className="reveal message-modal js-sms-message-modal" data-v-offset="" data-close-on-click="false" style={{ display: 'block' }} role="dialog" aria-hidden="false" data-yeti-box="login-and-create-account-popUp-modal" data-resize="login-and-create-account-popUp-modal" data-e="ucpld6-e" tabIndex={-1} data-events="resize">
      <div className="message-modal__inner">
        <h2 className="message-modal__heading">{i18n.getText('sms_popup_heading')}</h2>
        <div className="message-modal__input">
          <input className={`message-modal__number js-sms-phoneNum ${countryCodeNumberClass} ${inputInfo.isRedBackground && 'redBackground'}`} value={phoneNum} onChange={(e: React.ChangeEvent<HTMLInputElement>): void => handleInput(e)} onKeyPress={(e): void => handleAddSpace(e)} onBlur={(e: React.ChangeEvent<HTMLInputElement>): void => formatAndValidatePhoneNum(e)} maxLength={14} type="tel" placeholder="Enter mobile phone number" name="" size={10} data-type="customerPhone" />
          {smsPopupInfo?.countryCode === 'UK' ?
            <>
              <span className="message-modal__country message-modal__country--uk"></span>
              <span className="message-modal__code">+{countryPhoneCode.UK}</span>
            </> : <>
              <span className="message-modal__country message-modal__country--us"></span>
              <span className="message-modal__code">+{countryPhoneCode.US}</span>
            </>
          }
        </div>
        <div className="message-modal__oversight">
          {
            inputInfo?.isNumberError && <span className="message-modal__error  js-only-numbers-error">{i18n.getText('sms_popup_numbers_error')}</span>
          }
          {
            inputInfo?.isAreaCodeError && <span className="message-modal__error  js-area-code-error">{i18n.getText('sms_popup_area_code_error')}</span>
          }
          {
            inputInfo?.isPhoneNumError && <span className="message-modal__error js-valid-ten-digit-error">{i18n.getText('sms_popup_valid_ten_digit_error')}</span>
          }
          {
            inputInfo?.isAPIError && <span className="message-modal__error js-api-error">{i18n.getText('sms_popup_valid_api_error')}</span>
          }
        </div>
        <div className="message-modal__text">{parse(i18n.getText('sms_popup_confirm_disclaimer', smsPopupInfo))}</div>
        <div className="message-modal__btn">
          <button className="base-button base-button--full-width base-button--activation message-modal__btn--primary js-sms-confirm-btn sj-button" data-testid= "confirm-btn" type="submit" value="Confirm" onClick={handleConfirmBtn}>{i18n.getText('sms_popup_confirm_btn')}</button>
          <button className="base-button base-button--full-width base-button--inverted message-modal__btn--secondary js-sms-no-thanks-btn sj-button" type="submit" value="No Thanks" onClick={handleNoThanks}>{i18n.getText('sms_popup_no_thanks_btn')}</button>
        </div>
      </div>
    </div>
  );
};

export default memo(SMSPopup);
